import { Type } from "class-transformer";
import { DatabaseUser } from "src/modules/database/shared/database-user.entity";
import { CustomInitializer } from "src/modules/sm-base/shared/custom-initializer.interface";
import { Arrays } from "src/modules/utils/shared/arrays";
import { Dates } from "src/modules/utils/shared/dates";
import { Objects } from "src/modules/utils/shared/objects";
import { ProjectPlanningService } from "../services/project-planning.service";
import { CalendarEventInstance } from "./CalendarEventInstance";
import { CalendarEventKind } from "./CalendarEventKind";
import { CalendarEventParticipant } from "./CalendarEventParticipant";
import { CalendarEventReminderType } from "./CalendarEventReminderType";
import { CalendarEventResource } from "./CalendarEventResource";
import { CalendarEventRole } from "./CalendarEventRole";
import { CalendarEventSource } from "./CalendarEventSource";
import { CalendarEventStartRelativeTo } from "./CalendarEventStartRelativeTo";
import { CalendarEventSubTask } from "./CalendarEventSubTask";
import { CalendarEventType } from "./CalendarEventType";
import { Project } from "./Project";
import { ProjectPlanningTools } from "../models/project-planning-tools.model";

export class CalendarEvent implements CustomInitializer {

    static forProject(p: Project, startDate: Date = null): CalendarEvent {
        startDate = Dates.startOf(startDate ?? new Date(), "day");
        return Objects.fromPlain(CalendarEvent, {
            projectId: p?.id,
            kind: null,
            allDay: true,
            startDate,
            endDate: Dates.add(startDate, "day", 1)            
        });
    }
 
    //netgen start
    title: string;
    typeId = 0;
    @Type(() => CalendarEventType)
    type: CalendarEventType;
    contactPersonId: number = null;
    organisatorId: number = null;
    @Type(() => Date)
    startDate: Date;
    @Type(() => Date)
    endDate: Date;
    allDay = false;
    kind: CalendarEventKind = CalendarEventKind.appointment;
    finished = false;
    note: string;
    location: string;
    plannedTime = 0;
    doneTime = 0;
    isPrivate = false;
    reminderType: CalendarEventReminderType = CalendarEventReminderType.none;
    reminderDeltaMinutes = 0;
    reminderUsersDone: number[];
    @Type(() => CalendarEventParticipant)
    participants: CalendarEventParticipant[];
    @Type(() => CalendarEventInstance)
    instances: CalendarEventInstance[];
    @Type(() => CalendarEventResource)
    resources: CalendarEventResource[];
    outlookICalUId: string;
    @Type(() => Date)
    outlookOriginalStart: Date;
    source: CalendarEventSource = CalendarEventSource.local;
    watchersIds: number[];
    projectId: number = null;
    structurePath: string;
    startRelativeDays = 0;
    startRelativeTo: CalendarEventStartRelativeTo = null;
    startRelativeToEventId: number = null;
    onlyWeekdays = false;
    @Type(() => CalendarEventRole)
    roles: CalendarEventRole[];
    @Type(() => CalendarEventSubTask)
    subTasks: CalendarEventSubTask[];
    @Type(() => Date)
    creationDate: Date;
    createdBy: string;
    @Type(() => Date)
    modifyDate: Date;
    modifiedBy: string;
    id = 0;
    //netgen end

    customInitializer(): void {
        this.roles ??= [];
        this.subTasks ??= [];
        this.resources ??= [];
        this.participants ??= [];
        this.instances ??= [];
    }

    formatDate(date: Date): string {
        return this.allDay ? Dates.formatDefaultDate(date) : Dates.formatNoSeconds(date);
    }

    getDisplayEndDate(): Date {
        return this.allDay ? Dates.add(this.endDate, "day", -1) : this.endDate;
    }

    getDisplayEndDateComparable(): string {
        return Dates.formatDateInternational(this.getDisplayEndDate());
    }

    getDisplayCalendarId(): number {
        //TODO INSTANCE
        return 0;
    }

    getStructurePathTitles(service: ProjectPlanningService): string[] {
        return service.formatStructurePath(this.structurePath);
    }

    getAllEditors(): number[] {
        return Arrays.getUnique((this.roles ?? []).map(r => r.responsibleId));
    }

    getAllEditorsText(service: ProjectPlanningService, boldRoles = false): string {
        return service.getAllEditorsText([this], boldRoles);
        /*
        if (this.kind == CalendarEventKind.appointment) {
            return Arrays.itemsToString(Arrays.sort(this.instances.map(i => service.getUserShortName(service.users.find(u => u.calendarId == i.calendarId)?.id)).filter(s => s != "")), ", ");
        }

        let rolesIds: number[] = [];
        let userIds: number[] = [];
        for (let r of this.roles ?? []) {
            if (r.responsibleId == null) {
                rolesIds.push(r.roleId);
            }
            else {
                userIds.push(r.responsibleId);
            }
        }
        let roleStrings = Arrays.sort(Arrays.getUnique(rolesIds).map(id => service.roles.find(r => r.id == id)?.title ?? "???")); 
        let userStrings = Arrays.sort(Arrays.getUnique(userIds).map(id => service.getUserShortName(id)));
        return Arrays.itemsToString(roleStrings, ", ", r => boldRoles ? "<b>" + r + "</b>" : r) + (roleStrings.length > 0 && userStrings.length > 0 ? ", " : "") + Arrays.itemsToString(userStrings, ", ");
   */ }

    getAllResourcesText(service: ProjectPlanningService): string {
        return Arrays.itemsToString(Arrays.sort((this.resources ?? []).map(r => service.resources.find(res => res.id == r.resourceId)?.title ?? "<???>")), ", ");
    }

    getDateIntervalText(): string {
        return this.kind == CalendarEventKind.milestone ? 
            Dates.eq(this.startDate, this.endDate) ? Dates.formatDefaultDate(this.startDate) : Dates.formatDefaultDate(this.startDate) + " - " + Dates.formatDefaultDate(this.endDate) :  
            Dates.eq(this.startDate, this.endDate) ? Dates.formatDefault(this.startDate) : Dates.formatDefault(this.startDate) + " - " + Dates.formatDefault(this.endDate);
    }

    getStartDateForFullCalendar(): Date | string {
        return this.kind == CalendarEventKind.milestone ? this.getStartDateComparable() : this.startDate;
    }

    getStartDateComparable(): string {
        return Dates.formatDateInternational(this.startDate);
    }

    getEndDateForFullCalendar(): Date | string {
        return this.kind == CalendarEventKind.milestone ? this.getEndDateComparable() : this.endDate;
    }

    getEndDateComparable(): string {
        return Dates.formatDateInternational(this.endDate);
    }

    getStartRelativeToEventId(): number {
        return this.startRelativeTo == CalendarEventStartRelativeTo.task ? this.startRelativeToEventId : null;
    }

    isEditor(userId: number): boolean {
        return this.roles?.some(r => r.responsibleId == userId);
    }

    isDoneTimeAllowed(): boolean {
        return this.kind == CalendarEventKind.task;
    }

    isSetDoneTimeAllowed(user: DatabaseUser): boolean {
        return this.isDoneTimeAllowed() && !this.finished && (user.isAdmin || this.isEditor(user.id)); 
    }

    needsDoneTimeBeforeFinish(user: DatabaseUser): boolean {
        return this.kind == CalendarEventKind.task && this.plannedTime > 0 && !user.isAdmin;
    }

    isCopyAllowed(user: DatabaseUser): boolean {
        return !this.finished && (ProjectPlanningTools.isUserAtLeastController(user) || this.isEditor(user.id));
    }

    isEditAllowed(user: DatabaseUser): boolean {
        return this.kind != CalendarEventKind.appointment && !this.finished && (ProjectPlanningTools.isUserAtLeastController(user) || this.isEditor(user.id));
    }

    isFinishAllowed(user: DatabaseUser): boolean {
        return this.kind != CalendarEventKind.appointment && !this.finished && (ProjectPlanningTools.isUserAtLeastController(user) || this.isEditor(user.id));
    }

    isReopenAllowed(user: DatabaseUser): boolean {
        return this.kind != CalendarEventKind.appointment && this.finished && (ProjectPlanningTools.isUserAtLeastController(user) || this.isEditor(user.id));
    }

    isDeleteAllowed(user: DatabaseUser): boolean {
        return this.source != CalendarEventSource.outlook && (ProjectPlanningTools.isUserAtLeastController(user) || this.isEditor(user.id));
    }

    prepareForCopy(): void {
        this.id = 0;
        for (let role of this.roles) {
            role.id = 0;
            role.calendarEventId = 0;
        } 
        for (let st of this.subTasks) {
            st.id = 0;
            st.calendarEventId = 0;
        }
        for (let res of this.resources) {
            res.id = 0;
            res.calendarEventId = 0;
        }
        
        for (let instance of this.instances) {
            instance.id = 0;
            instance.calendarEventId = 0;
        }
        this.source = CalendarEventSource.local;
    }

    isFinished(): boolean {
        return this.kind == CalendarEventKind.appointment ? this.endDate < Dates.withoutTime(new Date()) : this.finished;
    }

    participantsEqual(other: CalendarEvent): boolean {
        return this.participants.length == other.participants.length && Arrays.equalsFlatUnordered(this.participants.map(p => p.userId + "|||" + p.mail), other.participants.map(p => p.userId + "|||" + p.mail));
    }

    rolesEqual(other: CalendarEvent): boolean {
        return this.roles.length == other.roles.length && Arrays.equalsFlatUnordered(this.roles.map(p => p.responsibleId + "|||" + p.roleId), other.roles.map(p => p.responsibleId + "|||" + p.roleId));
    }

}
